import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import { openDB } from 'idb';
import localStorageAvailable from '@raas/shared/src/utils/localStorageAvailable';
//
import { useLocales } from '@raas/shared/src/locales';
import usersService from '@raas/shared/src/service/api/UsersService';
import getClientInfo from '@raas/shared/src/utils/clientInfo';
import {
  isValidToken,
  setSession,
  mapUser,
  tokenRefresh,
  jwtDecode,
  clearCookie,
} from '@raas/shared/src/auth/utils';
import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  JWTContextType,
} from '@raas/shared/src/auth/types';
import { clearCache } from '@raas/shared/src/auth/serviceWorker';
import { useApplicationInsightsContext } from '@raas/shared/src/contexts/ApplicationInsightsContext';
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
    authPlatform: string;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  authPlatform: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
      authPlatform: action.payload.authPlatform,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      authPlatform: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

const setIdbForServiceWorker = async (token: string) => {
  const db = await openDB('appDB', 1, {
    upgrade(upgradeDb) {
      console.debug('Upgrade callback');
      if (!upgradeDb.objectStoreNames.contains('token')) {
        console.debug('create store');
        upgradeDb.createObjectStore('token', { keyPath: 'key' });
      }
    },
  });

  try {
    const tx = db.transaction('token', 'readwrite');
    const objectStore = tx.objectStore('token');
    await objectStore.put({ key: 'token', token });
    tx.commit();
    console.debug('Updated token store');
  } catch (error) {
    console.error('Error during transaction:', error);
  } finally {
    if (db) {
      try {
        db.close();
        console.debug('Database connection closed successfully');
      } catch (error) {
        console.error('Error while closing the database connection:', error);
      }
    }
  }
};

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
  loginPath: string;
  requiredPermission: string;
};

export function AuthProvider({ children, loginPath, requiredPermission }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();
  const { setUserContext, clearUserContext, trackEvent, trackException } =
    useApplicationInsightsContext();

  const { translate } = useLocales();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      let user;

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken, loginPath, requiredPermission, true);
        setIdbForServiceWorker(accessToken);

        const profile = await usersService.getProfile();
        user = mapUser(profile);
        setUserContext(user.id);

        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable, loginPath, requiredPermission, setUserContext]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (email: string, password: string) => {
      const loginStartTime = Date.now();
      const clientInfo = await getClientInfo();
      try {
        const { accessToken, user } = await usersService.login(email, password);

        setSession(accessToken, loginPath, requiredPermission, true);

        setUserContext(user.id);

        setIdbForServiceWorker(accessToken);

        dispatch({
          type: Types.LOGIN,
          payload: {
            user: mapUser(user),
            authPlatform: 'local',
          },
        });

        trackEvent('Auth_Login_Success', {
          method: 'local',
          userId: user.id,
          userName: `${user.firstName} ${user.lastName}`,
          permissions: requiredPermission || 'none',
          ip: clientInfo.ipAddress,
          country: clientInfo.country,
          city: clientInfo.city,
          timezone: clientInfo.timezone,
          utcOffset: clientInfo.utc_offset,
          language: clientInfo.language,
          loginDuration: Math.floor((Date.now() - loginStartTime) / 1000),
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        trackEvent('Auth_Login_Failed', {
          method: 'local',
          error: error?.message || 'Unknown error',
          ip: clientInfo.ipAddress,
          country: clientInfo.country,
          city: clientInfo.city,
          timezone: clientInfo.timezone,
          utcOffset: clientInfo.utc_offset,
          language: clientInfo.language,
          loginDuration: Math.floor((Date.now() - loginStartTime) / 1000),
          timestamp: new Date().toISOString(),
        });

        trackException(error as Error, {
          operation: 'login',
          method: 'local',
          email,
        });
        throw new Error(translate('common.invalidCredentials'));
      }
    },
    [translate, loginPath, requiredPermission, trackEvent, trackException, setUserContext]
  );

  // LOGOUT
  const logout = useCallback(async () => {
    clearCookie();
    setSession(null, loginPath, undefined, false);
    clearUserContext();
    dispatch({
      type: Types.LOGOUT,
    });
    // Deleting database is slower than resetting token which can cause a race condition that causes HTTP 401 on images.
    setIdbForServiceWorker('');
    clearServiceWorkerCache();
  }, [loginPath, clearUserContext]);

  const loginWithAccessToken = useCallback(
    async (token: string) => {
      const loginStartTime = Date.now();
      const clientInfo = await getClientInfo();
      try {
        const { accessToken, user, platform } = await usersService.createOrLogin(token);
        setSession(accessToken, loginPath, requiredPermission, false);
        setUserContext(user.id);
        const { exp } = jwtDecode(token);
        if (platform === 'azure') {
          tokenRefresh(exp, loginPath, requiredPermission, setIdbForServiceWorker, logout);
        }
        setIdbForServiceWorker(accessToken);
        dispatch({
          type: Types.LOGIN,
          payload: {
            user: mapUser(user),
            authPlatform: platform,
          },
        });
        trackEvent('Auth_Login_Success', {
          method: 'token',
          platform,
          userId: user.id,
          userName: `${user.firstName} ${user.lastName}`,
          permissions: requiredPermission || 'none',
          ip: clientInfo.ipAddress,
          country: clientInfo.country,
          city: clientInfo.city,
          timezone: clientInfo.timezone,
          utcOffset: clientInfo.utc_offset,
          language: clientInfo.language,
          loginDuration: Math.floor((Date.now() - loginStartTime) / 1000),
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        trackEvent('Auth_Login_Failed', {
          method: 'token',
          ip: clientInfo.ipAddress,
          country: clientInfo.country,
          city: clientInfo.city,
          timezone: clientInfo.timezone,
          utcOffset: clientInfo.utc_offset,
          language: clientInfo.language,
          error: error?.message || 'Unknown error',
          loginDuration: Math.floor((Date.now() - loginStartTime) / 1000),
          timestamp: new Date().toISOString(),
        });

        trackException(error as Error, {
          operation: 'loginWithAccessToken',
          method: 'token',
        });
        throw new Error(translate('common.invalidCredentials'));
      }
    },
    [translate, loginPath, requiredPermission, logout, trackEvent, trackException, setUserContext]
  );

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      // ToDo: implement when required
      dispatch({
        type: Types.REGISTER,
        payload: {
          user: null,
        },
      });
    },
    []
  );

  // CHANGE PASSWORD
  const changePassword = useCallback(
    async (email: string, password: string, newPassword: string) => {
      await usersService.changePassword(email, password, newPassword);
    },
    []
  );

  // SIGN UP
  const signUp = useCallback(
    async (token: string) => {
      try {
        await usersService.signUp(token);
      } catch {
        throw new Error(translate('common.invalidCredentials'));
      }
    },
    [translate]
  );

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      loginWithGoogle: () => {},
      loginWithGithub: () => {},
      loginWithTwitter: () => {},
      loginWithAccessToken,
      register,
      signUp,
      changePassword,
      logout,
      authPlatform: state.authPlatform,
    }),
    [
      state.isInitialized,
      state.isAuthenticated,
      state.user,
      login,
      loginWithAccessToken,
      register,
      signUp,
      changePassword,
      logout,
      state.authPlatform,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

function clearServiceWorkerCache() {
  clearCache();
}
